import {
    Button, Input, Tooltip, Upload,
} from 'antd';
import {FormInstance} from 'antd/es/form';
import {RcFile, UploadFile, UploadProps} from 'antd/es/upload/interface';
import React, {useState, FC} from 'react';
import {useSelector} from 'react-redux';

import {ActionConfirmModal} from 'components/table/action/action-confirm-modal/action-confirm-modal';
import {
    DEFAULT_DELETE_ELEMENT_CONFIRM_TEXT,
} from 'components/table/action/button-action-delete-tab/constants/button-delete-tab-constants';
import {deleteFile} from 'components/table/columns/file/file-column-utils';
import {IFieldMeta, IAdditionalOptions} from 'entities/metadata';
import {DATA_MODULE} from 'modules/data';
import {ReactComponent as CloseX} from 'shared/assets/forms/delete-x.svg';
import {ReactComponent as File} from 'shared/assets/forms/file.svg';
import {ReactComponent as LinkIcon} from 'shared/assets/link.svg';
import {EEntityType} from 'shared/constants/entities';
import {ECommonMessages} from 'shared/constants/messages';
import {downloadFile, showMessageFromResponse} from 'shared/utils';
import {TAppState} from 'store/config/types';

import {FileFieldInfo} from './file-field-info';
import {CUSTOM_SETTINGS, DEFAULT_SETTINGS} from './upload-file-field-constants';
import {generateDeleteLink, generateDownloadLink} from './upload-file-field-utils';
import './upload-file-field.less';

export interface IFileFieldProps {
    onChange?: (value: any) => void;
    fieldKey: string;
    form?: FormInstance;
    entityName: string;
    fieldType: string;
    buttonText?: string;
    shouldFastUpdate?: boolean;
    accept?: string;
    fieldMeta: IFieldMeta;
    parentEntityName: string;
    dependentsFieldKey: string;
    additionalOptions?: IAdditionalOptions;
}

/**
 * Поле реализовано на основе текущих нужд серверной части
 */

export const UploadFileField: FC<IFileFieldProps> = (
    {
        onChange,
        buttonText = 'Выбрать Файл',
        accept,
        dependentsFieldKey,
        fieldMeta,
        entityName,
        form,
        fieldKey,
        ...props
    }: IFileFieldProps,
) => {
    const defaultValue = form?.getFieldValue(fieldKey);
    const settings = {...DEFAULT_SETTINGS, ...CUSTOM_SETTINGS[fieldMeta?.dependentsFieldKey ?? '']};
    const [fileList, setFileList] = useState<UploadFile<Blob>[]>(defaultValue ? [defaultValue] : []);
    const linkDownload = generateDownloadLink(fieldMeta.key, fieldMeta.referenceUrl);
    const linkDelete = generateDeleteLink(fieldMeta.key, fieldMeta.referenceUrl);
    const [infoField, setInfoField] = useState<any>(
        useSelector(
            (state: TAppState) => state[DATA_MODULE][entityName]?.[EEntityType.FORM]?.data?.[dependentsFieldKey],
        ),
    );

    const handleChange: UploadProps['onChange'] = info => {
        const newFileList = [...info.fileList];
        if (newFileList.length > 1) newFileList.shift();
        if (onChange) onChange(newFileList[0]?.originFileObj);
        setFileList(newFileList);
    };
    const beforeUpload = (file: UploadFile<Blob>) => {
        const formData = new FormData();
        formData.append('file', file as RcFile);
        return false;
    };
    const onFileDownload = () => {
        downloadFile(linkDownload, infoField?.fileName).then(data => {
            if (data.status === 200) {
                showMessageFromResponse({
                    response: data,
                    isError: false,
                    customMessage: ECommonMessages.UPLOAD_FILE_SUCCESS,
                });
            }
        }).catch(error => {
            showMessageFromResponse({response: error?.response, isError: true});
        });
    };
    const onFileDelete = () => {
        deleteFile(linkDelete).then(data => {
            if (data.status === 200) {
                showMessageFromResponse({response: data, isError: false});
                setInfoField(null);
                setFileList([]);
            }
        }).catch(error => {
            const isFileNotFound = error?.response?.status === 404;

            if (isFileNotFound) {
                // сброс состояния в случае если файл уже был удален ранее
                setInfoField(null);
                return;
            }

            showMessageFromResponse({response: error?.response, isError: true});
        });
    };

    const showFilePath = fileList.length ? fileList[0].name : '';

    return (
        infoField && !settings?.enableReuploading ? (
            <ActionConfirmModal
                cancelText="Отмена"
                approveText="Удалить"
                modalTitle="Удаление"
                confirmModalContent={DEFAULT_DELETE_ELEMENT_CONFIRM_TEXT}
                confirmAction={onFileDelete}
            >
                {handleClick => (
                    <FileFieldInfo
                        className="upload-field__info"
                        onFileDelete={handleClick}
                        onFileDownload={onFileDownload}
                        infoField={infoField}
                        name={infoField?.fileName}
                        alwaysAllowDownload
                    />
                )}

            </ActionConfirmModal>
        ) : (
            <Upload
                {...props}
                accept={accept}
                beforeUpload={beforeUpload}
                onChange={handleChange}
                showUploadList={false}
                fileList={fileList}
                className="upload-field"
            >
                <div className="upload-field-wrapper">
                    <Input
                        disabled
                        prefix={<File />}
                        className="ant-input--soft-disabled"
                        suffix={(
                            <>
                                {showFilePath && (
                                    <CloseX
                                        className="upload-field__clear-icon"
                                        onClick={e => {
                                            e.stopPropagation();
                                            if (onChange) onChange(null);
                                            setFileList([]);
                                        }}
                                    />
                                )}
                                {!showFilePath && infoField && (
                                    <Tooltip
                                        className="download-icon"
                                        title={infoField?.signName}
                                    >
                                        <LinkIcon onClick={e => {
                                            e.stopPropagation();
                                            onFileDownload();
                                        }}
                                        />
                                    </Tooltip>
                                )}
                            </>
                        )}
                        value={showFilePath || infoField?.fileName || 'Файл не прикреплен'}
                    />
                    <Button className="upload-field__btn">
                        {buttonText}
                    </Button>
                </div>
            </Upload>
        )
    );
};
